// src/pages/markets/board/api/useEventOutcomes.ts
//
// The board endpoint cannot name the yes side of a head-to-head market — its
// outcome_yes_label is Polymarket's groupItemTitle. /api/event-markets ships
// the real outcomes pair, so the card fetches it per event and hands it to the
// naming rules. Outcome names never change, so this is cached hard and the
// labels simply sharpen when it lands.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../api/apiClient';
import { boardKeys } from './boardKeys';
import type { EventMarketsResponse } from '../../api/types';

const EMPTY: ReadonlyMap<string, string[]> = new Map();
const ONE_HOUR = 60 * 60 * 1000;

/**
 * condition_id (the board's market_id) → the market's true outcome pair.
 * The two ids being the same is an observed invariant, not a documented one:
 * verified 35/35 against event 830257 (LNG Esports vs Weibo Gaming).
 */
export function outcomesByMarket(
    res: EventMarketsResponse | null | undefined
): ReadonlyMap<string, string[]> {
    if (!res?.markets) return EMPTY;
    const map = new Map<string, string[]>();
    for (const m of res.markets) {
        const id = m.condition_id;
        const o = m.outcomes;
        if (!id || !o || o.length !== 2) continue;
        if (!o.every(x => typeof x === 'string' && x.trim() !== '')) continue;
        map.set(id, o);
    }
    return map;
}

export function useEventOutcomes(
    eventId: string | null,
    enabled = true
): ReadonlyMap<string, string[]> {
    const active = enabled && Boolean(eventId);
    const query = useQuery({
        queryKey: boardKeys.eventOutcomes(eventId ?? ''),
        queryFn: ({ signal }) =>
            apiGet<EventMarketsResponse>(
                `/api/event-markets/${encodeURIComponent(eventId as string)}`,
                signal
            ),
        enabled: active,
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
        retry: 1,
    });

    return useMemo(() => outcomesByMarket(query.data), [query.data]);
}
